import styles from './nav-menu.module.scss';
import { Button, Menu } from 'antd';
import type { MenuProps } from 'antd';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '@ant-design/icons';
import PowerIcon from '@/assets/icons/power-01.svg?react';
import { useLogout } from '@/hooks/use-logout/use-logout';
import {
  NavGroup,
  Section,
  VISIBLE_SECTIONS,
  canAccess,
  findSectionByPath,
} from '@/config/navigation';
import { useCompaniesList } from '@/hooks/use-company/use-company';
import { useUser } from '@/hooks/use-user/use-user';

interface NavMenuProps {
  onMenuItemClick?: () => void;
  collapsed?: boolean;
}

export function NavMenu({ onMenuItemClick, collapsed = false }: NavMenuProps) {
  const { t } = useTranslation('navigation');
  const { logout } = useLogout();
  const { pathname } = useLocation();
  const { userData } = useUser();
  const { companies } = useCompaniesList();

  const selectedKey = findSectionByPath(pathname)?.key ?? '';

  const sectionIcon = (section: Section) => <Icon component={section.icon} />;

  const sectionItem = (section: Section) => ({
    key: section.key,
    label: (
      <NavLink onClick={onMenuItemClick} to={section.path}>
        {t(`sections.${section.key}.label`)}
      </NavLink>
    ),
    icon: sectionIcon(section),
  });

  const groupItem = (group: NavGroup) => {
    const sections = VISIBLE_SECTIONS.filter(
      (section) => section.group === group && canAccess(section, userData, companies)
    );

    // AntD renders a group's label even with no children, which would leave a heading
    // over nothing for a role that cannot reach any section in it.
    if (!sections.length) return null;

    return {
      key: group,
      type: 'group' as const,
      label: t(`groups.${group}`),
      children: sections.map(sectionItem),
    };
  };

  // Logout sits outside both groups so it stays a direct child of the menu root,
  // where the `:last-child { margin-top: auto }` rule can still pin it to the bottom.
  const logoutItem = {
    key: 'logout',
    label: (
      <Button
        role="button"
        className={styles.logoutButton}
        type="link"
        onClick={async () => await logout()}
      >
        <Icon component={PowerIcon} />
        {t('logout')}
      </Button>
    ),
  };

  const items: MenuProps['items'] = [groupItem('main'), groupItem('config'), logoutItem].filter(
    (item) => item !== null
  );

  return (
    <Menu
      // inline + inlineCollapsed lets AntD hide labels and shrink the
      // menu to icon-only when the sidebar is collapsed, with built-in
      // tooltips on hover. mode stays "inline" even when expanded so the
      // collapse transition is animated cleanly.
      mode="inline"
      inlineCollapsed={collapsed}
      selectedKeys={[selectedKey]}
      className={styles.menu}
      items={items}
    />
  );
}
